/**
 * shoppable-video.js
 * Shoppable Video — Thumbnail Carousel + Floating Popup Player
 *
 * LOADING
 *  - Zero video bytes are requested until the page has fully loaded
 *    (window 'load', then an idle callback). This keeps video weight
 *    entirely out of FCP / LCP.
 *  - Videos then download strictly ONE AT A TIME: thumbs in order,
 *    then the popup slides. Never two requests in flight at once.
 *  - Downloading is completely independent of playback — it keeps
 *    going while videos are paused, off-screen, or while the popup
 *    is open.
 *
 * PLAYBACK (thumbnails)
 *  - All loaded thumbs play together, but only while the section is
 *    in the viewport.
 *  - Scroll away → they pause, downloads continue.
 *  - Popup opens → all thumbs pause. Popup closes → they resume.
 *  - Tab hidden → pause (battery/CPU), resume on return.
 *
 * No <video> carries a real `src` until JS assigns one from `data-src`.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     STATE
  ───────────────────────────────────────────── */
  var thumbSwiper      = null;
  var popupCarousel    = null;
  var currentIndex     = 0;
  var isMuted          = true;
  var progressCleanup  = null;
  var totalSlides      = 0;
  var bodyOverflowPrev = '';

  var sectionEl     = null;
  var thumbVidEls   = [];
  var thumbEls      = [];

  var sectionInView = false;
  var popupOpen     = false;

  var loadQueue = [];
  var queueBusy = false;

  var popup, trackWrap, track, slides, popupVids, muteButtons, progressFills;

  /* ─────────────────────────────────────────────
     LAZY LOADER
     Assigns .src from data-src exactly once, and fires the callback
     when the video has buffered enough to start playing.
     data-loading / data-loaded are inspectable in DevTools.
  ───────────────────────────────────────────── */
  function loadVideoSrc(vid, cb) {
    if (!vid) { if (cb) cb(); return; }

    if (vid.getAttribute('data-loaded') === 'true') { if (cb) cb(); return; }

    var dataSrc = vid.getAttribute('data-src');
    if (!dataSrc) { if (cb) cb(); return; }

    /* Already in flight — piggyback rather than starting a 2nd request */
    if (vid.getAttribute('data-loading') === 'true') {
      if (cb) {
        vid.addEventListener('loadeddata', function once() {
          vid.removeEventListener('loadeddata', once);
          cb();
        });
      }
      return;
    }

    vid.setAttribute('data-loading', 'true');

    var done = false;
    function onReady() {
      if (done) return;
      done = true;
      vid.removeAttribute('data-loading');
      vid.setAttribute('data-loaded', 'true');
      vid.removeEventListener('loadeddata', onReady);
      vid.removeEventListener('error', onReady);
      if (cb) cb();
    }

    vid.addEventListener('loadeddata', onReady);
    vid.addEventListener('error', onReady); /* a bad file must not stall the queue */

    vid.preload = 'auto';
    vid.src = dataSrc;
    vid.load();
  }

  /* ─────────────────────────────────────────────
     SEQUENTIAL DOWNLOAD QUEUE
     Thumbs first (in order), then popup slides. The popup videos
     point at the same CDN URLs as the thumbs, so by the time the
     queue reaches them they normally resolve straight from the
     browser's HTTP cache.
  ───────────────────────────────────────────── */
  function buildLoadQueue() {
    loadQueue = [];
    thumbVidEls.forEach(function (v) { if (v) loadQueue.push(v); });
    popupVids.forEach(function (v) { if (v) loadQueue.push(v); });
  }

  function processLoadQueue() {
    if (queueBusy) return;

    var next = null;
    while (loadQueue.length) {
      var candidate = loadQueue.shift();
      if (candidate && candidate.getAttribute('data-loaded') !== 'true') {
        next = candidate;
        break;
      }
    }
    if (!next) return;

    queueBusy = true;
    loadVideoSrc(next, function () {
      queueBusy = false;
      markThumbReady(next);
      syncThumbPlayback(); /* a newly-ready thumb joins in if we're in view */
      processLoadQueue();
    });
  }

  /* Drop the blur + pause icon once a thumb actually has data */
  function markThumbReady(vid) {
    var i = thumbVidEls.indexOf(vid);
    if (i > -1 && thumbEls[i]) thumbEls[i].classList.remove('is-idle');
  }

  /* ─────────────────────────────────────────────
     PLAYBACK GATE
     One place decides whether thumbs should be playing.
  ───────────────────────────────────────────── */
  function syncThumbPlayback() {
    var shouldPlay = sectionInView && !popupOpen && !document.hidden;

    thumbVidEls.forEach(function (vid) {
      if (!vid) return;
      var isLoaded = vid.getAttribute('data-loaded') === 'true';

      if (shouldPlay && isLoaded) {
        if (vid.paused) {
          vid.muted = true; /* required for autoplay on iOS/Android */
          var p = vid.play();
          if (p && p.catch) p.catch(function () {});
        }
      } else if (!vid.paused) {
        vid.pause();
      }
    });
  }

  /* ─────────────────────────────────────────────
     INIT — DOM wiring only, no network
  ───────────────────────────────────────────── */
  function init() {
    sectionEl = document.getElementById('sivSection');
    popup     = document.getElementById('sivPopup');
    if (!sectionEl || !popup) return;

    document.body.appendChild(popup);

    trackWrap     = document.getElementById('sivTrackWrap');
    track         = document.getElementById('sivTrack');
    slides        = Array.prototype.slice.call(track.querySelectorAll('.siv-popup-slide'));
    popupVids     = slides.map(function (s) { return s.querySelector('.siv-popup-vid'); });
    muteButtons   = slides.map(function (s) { return s.querySelector('.siv-mute-btn'); });
    progressFills = slides.map(function (s) { return s.querySelector('.siv-progress-fill'); });
    totalSlides   = slides.length;

    initThumbs();
    initPopupSlider();
    initObservers();

    var sivAddBtn = document.querySelector('.siv-add-btn');
    if (sivAddBtn) {
      sivAddBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var atcBtn = document.querySelector('.product__info-container .product-form__submit');
        if (atcBtn) atcBtn.click();
        setTimeout(function () { closePopup(); }, 500);
      });
    }

    document.getElementById('sivClose').addEventListener('click', closePopup);
    document.getElementById('sivNavUp').addEventListener('click', function () { navigate(-1); });
    document.getElementById('sivNavDown').addEventListener('click', function () { navigate(1); });

    slides.forEach(function (slide, i) {
      if (muteButtons[i]) {
        muteButtons[i].addEventListener('click', function (e) {
          e.stopPropagation();
          toggleMute();
        });
      }
      if (popupVids[i]) {
        popupVids[i].addEventListener('click', function () { togglePlayPause(i); });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (!popup.classList.contains('is-visible')) return;
      if (e.key === 'Escape')    closePopup();
      if (e.key === 'ArrowUp')   navigate(-1);
      if (e.key === 'ArrowDown') navigate(1);
    });

    window.addEventListener('resize', debounce(function () {
      if (popupCarousel && popupCarousel.slider) popupCarousel.slider.update();
    }, 200));

    /* Network work waits for the page to finish loading */
    whenPageLoaded(function () {
      buildLoadQueue();
      processLoadQueue();
    });
  }

  /* ─────────────────────────────────────────────
     THUMBNAIL CAROUSEL
  ───────────────────────────────────────────── */
  function initThumbs() {
    var thumbsEl = document.getElementById('sivThumbs');

    function revealSection() {
      requestAnimationFrame(function () {
        if (sectionEl) sectionEl.style.opacity = '1';
      });
    }

    if (thumbsEl && window.FoxTheme && typeof window.FoxTheme.Carousel === 'function') {
      thumbSwiper = new window.FoxTheme.Carousel(thumbsEl, {
        slidesPerView: 2.3,
        spaceBetween: 18,
        watchSlidesProgress: true,
        navigation: {
          nextEl: document.querySelector('.siv-next-btn'),
          prevEl: document.querySelector('.siv-prev-btn')
        },
        breakpoints: { 768: { slidesPerView: 3 } },
        on: { init: revealSection }
      });
      thumbSwiper.init();
    } else if (typeof Swiper !== 'undefined' && thumbsEl) {
      thumbSwiper = new Swiper(thumbsEl, {
        slidesPerView: 2.3,
        spaceBetween: 18,
        watchSlidesProgress: true,
        navigation: { prevEl: '.siv-prev-btn', nextEl: '.siv-next-btn' },
        breakpoints: { 768: { slidesPerView: 4 } },
        on: { init: revealSection }
      });
    }

    /* Safety net — never leave the section stuck at opacity 0 */
    revealSection();

    thumbVidEls = Array.prototype.slice.call(document.querySelectorAll('.siv-thumb-vid'));
    thumbEls    = Array.prototype.slice.call(document.querySelectorAll('.siv-thumb'));

    /* Everything starts blurred with a pause icon until it has data */
    thumbEls.forEach(function (t) { t.classList.add('is-idle'); });

    thumbEls.forEach(function (thumb, i) {
      var idx = parseInt(thumb.getAttribute('data-index'), 10);
      if (isNaN(idx)) idx = i;

      thumb.addEventListener('click', function () { openPopup(idx); });
      thumb.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPopup(idx);
        }
      });
    });
  }

  /* ─────────────────────────────────────────────
     OBSERVERS — viewport + tab visibility
  ───────────────────────────────────────────── */
  function initObservers() {
    if ('IntersectionObserver' in window && sectionEl) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          sectionInView = entry.isIntersecting;
        });
        syncThumbPlayback();
      }, { threshold: 0.15, rootMargin: '100px 0px' });

      io.observe(sectionEl);
    } else {
      /* No IO support — treat as always in view */
      sectionInView = true;
    }

    document.addEventListener('visibilitychange', syncThumbPlayback);
  }

  /* ─────────────────────────────────────────────
     POPUP SLIDER
  ───────────────────────────────────────────── */
  function initPopupSlider() {
    if (!trackWrap || !(window.FoxTheme && typeof window.FoxTheme.Carousel === 'function')) {
      return;
    }

    popupCarousel = new window.FoxTheme.Carousel(trackWrap, {
      direction: 'vertical',
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 320,
      threshold: 4,
      resistanceRatio: 0.35,
      watchSlidesProgress: true,
      on: {
        slideChangeTransitionStart: function () {
          pauseCurrentVideo();
          resetProgress(currentIndex);
          stopProgress();
        },
        slideChangeTransitionEnd: function () {
          currentIndex = popupCarousel.slider.activeIndex;
          loadVideoSrc(popupVids[currentIndex], function () {
            playCurrentVideo();
            startProgress();
          });
          updateNavButtons();
          updateActiveThumbnail();
        }
      }
    });
    popupCarousel.init();
  }

  /* ─────────────────────────────────────────────
     POPUP — OPEN / CLOSE
  ───────────────────────────────────────────── */
  function openPopup(idx) {
    currentIndex = clamp(idx, 0, totalSlides - 1);

    popupOpen = true;
    syncThumbPlayback(); /* pauses every thumb */

    if (popupCarousel && popupCarousel.slider) {
      popupCarousel.slider.slideTo(currentIndex, 0);
    }

    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () { popup.classList.add('is-visible'); });

    bodyOverflowPrev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      /* User-initiated, so this one jumps the queue. Usually already
         cached from the thumb download of the same URL. */
      loadVideoSrc(popupVids[currentIndex], function () {
        playCurrentVideo();
        startProgress();
      });
      updateNavButtons();
      updateActiveThumbnail();

      var closeBtn = document.getElementById('sivClose');
      if (closeBtn) closeBtn.focus();
    });
  }

  function closePopup() {
    popup.classList.remove('is-visible');
    popup.setAttribute('aria-hidden', 'true');
    pauseAllPopupVideos();
    stopProgress();
    clearActiveThumbnail();

    document.body.style.overflow = bodyOverflowPrev;

    setTimeout(function () { popup.hidden = true; }, 300);

    popupOpen = false;
    syncThumbPlayback(); /* thumbs resume if still in view */
  }

  /* ─────────────────────────────────────────────
     NAVIGATION
  ───────────────────────────────────────────── */
  function navigate(dir) {
    if (!popupCarousel || !popupCarousel.slider) return;
    if (dir > 0) {
      popupCarousel.slider.slideNext();
    } else {
      popupCarousel.slider.slidePrev();
    }
  }

  /* ─────────────────────────────────────────────
     POPUP VIDEO PLAYBACK
  ───────────────────────────────────────────── */
  function playCurrentVideo() {
    var vid = popupVids[currentIndex];
    if (!vid) return;
    vid.muted = isMuted;
    var p = vid.play();
    if (p && p.catch) p.catch(function () {});
    if (slides[currentIndex]) slides[currentIndex].classList.remove('is-paused');
    syncMuteIcon(currentIndex);
  }

  function pauseCurrentVideo() {
    var vid = popupVids[currentIndex];
    if (vid) vid.pause();
  }

  function pauseAllPopupVideos() {
    popupVids.forEach(function (v) {
      if (v) { v.pause(); v.currentTime = 0; }
    });
    slides.forEach(function (s) { s.classList.remove('is-paused'); });
  }

  function togglePlayPause(idx) {
    var vid = popupVids[idx];
    if (!vid) return;
    if (vid.paused) {
      loadVideoSrc(vid, function () {
        var p = vid.play();
        if (p && p.catch) p.catch(function () {});
        slides[idx].classList.remove('is-paused');
        startProgress();
      });
    } else {
      vid.pause();
      slides[idx].classList.add('is-paused');
      stopProgress();
    }
  }

  /* ─────────────────────────────────────────────
     MUTE
  ───────────────────────────────────────────── */
  function toggleMute() {
    isMuted = !isMuted;
    var vid = popupVids[currentIndex];
    if (vid) vid.muted = isMuted;
    syncMuteIcon(currentIndex);
  }

  function syncMuteIcon(idx) {
    var btn = muteButtons[idx];
    if (!btn) return;
    var mutedIco = btn.querySelector('.siv-ico-muted');
    var soundIco = btn.querySelector('.siv-ico-sound');
    if (mutedIco) mutedIco.style.display = isMuted ? '' : 'none';
    if (soundIco) soundIco.style.display = isMuted ? 'none' : '';
  }

  /* ─────────────────────────────────────────────
     PROGRESS BAR
  ───────────────────────────────────────────── */
  function startProgress() {
    stopProgress();
    var vid  = popupVids[currentIndex];
    var fill = progressFills[currentIndex];
    if (!vid || !fill) return;

    function onTimeUpdate() {
      if (vid.duration && !isNaN(vid.duration)) {
        fill.style.width = ((vid.currentTime / vid.duration) * 100).toFixed(2) + '%';
      }
    }

    function onEnded() {
      fill.style.width = '100%';
      if (currentIndex < totalSlides - 1) {
        navigate(1);
      } else {
        stopProgress();
      }
    }

    vid.addEventListener('timeupdate', onTimeUpdate);
    vid.addEventListener('ended', onEnded);

    progressCleanup = function () {
      vid.removeEventListener('timeupdate', onTimeUpdate);
      vid.removeEventListener('ended', onEnded);
    };
  }

  function stopProgress() {
    if (typeof progressCleanup === 'function') progressCleanup();
    progressCleanup = null;
  }

  function resetProgress(idx) {
    if (progressFills[idx]) progressFills[idx].style.width = '0%';
  }

  /* ─────────────────────────────────────────────
     UI STATE
  ───────────────────────────────────────────── */
  function updateNavButtons() {
    var upBtn   = document.getElementById('sivNavUp');
    var downBtn = document.getElementById('sivNavDown');
    if (upBtn)   upBtn.disabled   = currentIndex === 0;
    if (downBtn) downBtn.disabled = currentIndex === totalSlides - 1;
  }

  function updateActiveThumbnail() {
    document.querySelectorAll('.siv-thumb').forEach(function (t, i) {
      t.classList.toggle('is-active', i === currentIndex);
    });
  }

  function clearActiveThumbnail() {
    document.querySelectorAll('.siv-thumb').forEach(function (t) {
      t.classList.remove('is-active');
    });
  }

  /* ─────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────── */
  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  function whenPageLoaded(fn) {
    function idle() {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(fn, { timeout: 3000 });
      } else {
        setTimeout(fn, 500);
      }
    }
    if (document.readyState === 'complete') {
      idle();
    } else {
      window.addEventListener('load', idle);
    }
  }

  /* ─────────────────────────────────────────────
     BOOT
     DOM wiring runs as soon as the DOM is ready so the carousel
     renders immediately; all video downloading is deferred inside
     init() via whenPageLoaded().
  ───────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
